import React from "react";
import {Paper, Typography, Box, Button} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {Course} from "../../../common/course";
import MultiFormatImage from '../../core/multiFormatImage';

type Props = {
    elevation?: number,
    onMouseEnterFn?: Function,
    onMouseLeaveFn?: Function,
    course: Course,
    square?: boolean,
    btnText?: string,
};
type CommonProps = Props & {
    type?: 'image' | 'card',
};
const CardRow = ({
    elevation,
    onMouseEnterFn,
    onMouseLeaveFn,
    square,
    course,
    btnText,
}: Props) => {
    if (!course) {
        return null;
    }
    return (
        <Paper
            elevation={elevation}
            onMouseEnter={onMouseEnterFn ? (e) => onMouseEnterFn(e) : undefined}
            onMouseLeave={onMouseLeaveFn ? (e) => onMouseLeaveFn(e) : undefined}
            square={square}
            sx={{
                display: 'grid',
                gridTemplateColumns: '200px 1fr 220px',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    paddingTop: '200px',
                    position: 'relative',
                    width: '200px',
                    overflow: 'hidden',
                }}
            >
                <MultiFormatImage
                    image={course.image}
                    alt={course.description}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        margin: 'auto',
                    }}
                />
            </Box>
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        width: '1px',
                        height: '70%',
                        backgroundColor: '#ccc',
                        position: 'absolute',
                        display: 'block',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        margin: 'auto',
                    }
                }}
            >
                    <Typography
                        component='h2'
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '1.4rem',
                            color: 'secondary.dark',
                            marginBottom: '0.5em',
                        }}
                    >
                        {course.title}
                    </Typography>
                    <Typography
                        sx={{
                            WebkitLineClamp: '3',
                            lineClamp: '3',
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            boxOrient: 'vertical',
                        }}
                    >
                        {course.description}
                    </Typography>
                    <Box
                        component={'progress'}
                        max={Math.max(course.results.maxScore, course.results.score)}
                        value={Math.min(course.results.maxScore, course.results.score)}
                        sx={{
                            marginTop: 'auto',
                            width: '100%',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            height: '10px',
                            '&[value]::-webkit-progress-bar': {
                                borderRadius: '10px',
                                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.25) inset',
                                backgroundColor: '#eee',
                                overflow: 'hidden',
                            },
                            '&[value]::-webkit-progress-value': {
                                backgroundColor: 'success.main',
                            },
                        }}
                    />
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <Button variant="contained">{btnText}</Button>
            </Box>
        </Paper>
    );
}

const CardImage = ({elevation, square, onMouseEnterFn, onMouseLeaveFn, course, btnText}: Props) => {
    return (
        <Paper
            elevation={elevation}
            onMouseEnter={onMouseEnterFn ? (e) => onMouseEnterFn(e) : undefined}
            onMouseLeave={onMouseLeaveFn ? (e) => onMouseLeaveFn(e) : undefined}
            square={square}
        >
            <MultiFormatImage
                image={course.image}
                alt={course.description}
                sx={{
                    maxWidth: '100%',
                    minWidth: '100%',
                    display: 'block',
                }}
            />
            <Box sx={{px: 4, py: 2}}>
                <Typography
                    component='h2'
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.4rem',
                        color: 'secondary.dark',
                        marginBottom: '0.5em',
                        textAlign: 'center',
                    }}
                >
                    {course.title}
                </Typography>
                <Typography
                        sx={{
                            mb: 2,
                        }}
                    >
                        {course.description}
                </Typography>
                <Box
                        component={'progress'}
                        max={Math.max(course.results.maxScore, course.results.score)}
                        value={Math.min(course.results.maxScore, course.results.score)}
                        sx={{
                            width: '100%',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            height: '10px',
                            '&[value]::-webkit-progress-bar': {
                                borderRadius: '10px',
                                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.25) inset',
                                backgroundColor: '#eee',
                                overflow: 'hidden',
                            },
                            '&[value]::-webkit-progress-value': {
                                backgroundColor: 'success.main',
                            },
                        }}
                    />
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2}}>
                    <Button variant="contained">{btnText}</Button>
                </Box>
            </Box>
        </Paper>
    )
}
const View = ({
    type = 'image',
    ...rest
}: CommonProps) => {
    const {t: translate, i18n: languageCtx} = useTranslation();
    const btnText = translate('translation:goToCourse');
    if (type === 'card') {
        return <CardRow btnText={btnText} {...rest}/>
    }
    return <CardImage btnText={btnText} {...rest} />
}

export default View;